import { useMemo } from "react";

import {
  useGetBranchConfigsQuery,
  useGetBranchCountryRulesQuery,
  useGetBranchPaymentMethodQuery,
  useGetBranchWpsQuery,
  type WpsReadinessIssue,
} from "@/services/(company)/branchConfigsApi";
import { MODULE_SETTINGS_PATHS as MS } from "@/routes/company/module-settings.paths";

/**
 * Whether a branch is configured enough to run payroll, how far through setup
 * it is, and if something is missing, exactly what and where to fix it.
 *
 * WHY IT LIVES IN `src/hooks/` rather than under the payroll settings route:
 * the run wizard and the settings branch cards both consume it, and nothing
 * outside `payrolls/settings/**` imports that folder's `_hooks` — that boundary
 * is real, so a shared predicate does not belong behind it.
 *
 * WHERE THE DATA COMES FROM. Preferably from `readiness` on the branch list
 * payload, which costs no extra requests. When that field is absent — an API
 * deployed before it existed — this falls back to the three per-branch
 * endpoints, which is what it used to do unconditionally and which cost 3N
 * requests to render a list of N branches. The fallback exists so the client
 * can ship independently of the API; it can be deleted once
 * `feat/branch-payroll-readiness` is live everywhere.
 *
 * WHAT IS DELIBERATELY NOT CHECKED: the Attendance / Leave / Penalties
 * sections. Those three ship working defaults and an unset one does not stop a
 * run, it only changes the numbers. Counting them would cry wolf on every
 * branch — and would hold a branch that is genuinely ready to run payroll below
 * 100% forever, since none of them has a "configured" state to reach.
 */

/** The `sTab` values in `BranchDetail.tsx`'s `TABS`, for the fix-it links. */
type BranchSettingsTab =
  "country-settings" | "payroll-policy" | "payment-method" | "wps";

export type BranchReadinessGap = {
  /**
   * Matches the sub-tab's label so the notice names what the user will see.
   * English DATA, translated at the display point through
   * `BRANCH_READINESS_LABEL_KEYS` — callers key maps off it, so it stays a
   * stable value rather than a translated string.
   */
  label: string;
  /**
   * What breaks if this stays unset — an i18n KEY, rendered with `t()` under
   * the label. A key rather than copy because this predicate has no translator
   * and the notice that shows it does.
   */
  consequence: string;
  /**
   * `true` when a run genuinely cannot proceed, so the caller can block
   * rather than warn: exactly what create refuses — no ACTIVE payroll policy,
   * and no ACTIVE default country rule (R21: the latter used to be a warning
   * while create 400'd on it).
   */
  blocking: boolean;
  /** Deep link onto the offending sub-tab of this branch. */
  href: string;
};

/** One requirement and whether this branch meets it. */
export type BranchReadinessCheck = BranchReadinessGap & { satisfied: boolean };

export type BranchPayrollReadiness = {
  /** `true` while the data behind the checks is still loading. */
  isLoading: boolean;
  gaps: BranchReadinessGap[];
  /** Gaps that make a run impossible, not merely incomplete. */
  blockingGaps: BranchReadinessGap[];
  /** `true` once loaded with nothing missing. */
  isReady: boolean;
  /** Requirements met, and how many there are. `0 / 0` while loading. */
  completedCount: number;
  totalCount: number;
  /**
   * `completedCount / totalCount` as a whole percent — only ever 0, 25, 50, 75
   * or 100, because there are four checks. Deliberately coarse rather than
   * fabricating precision from sub-fields nobody set out to complete.
   */
  percentComplete: number;
  /** Branch configuration root for this branch, for a general "open settings" link. */
  settingsHref: string;
  /**
   * The readiness data could not be read (e.g. a 403 for a role without the
   * payroll rights the endpoint asks for). Nothing is known, so NOTHING is
   * reported missing and nothing blocks — the server still enforces every
   * check on create. Reading a failed request as "no policy" is what made the
   * run wizard unusable for a payroll operator (R15).
   */
  isUnavailable: boolean;
  /**
   * The branch's pay periods leave days out — a policy saved before that was
   * refused (D3). A warning: runs still work, but those days are never paid
   * or docked.
   */
  payrollPeriodIssue: "SAME_MONTH_GAP" | "CROSS_MONTH_GAP" | null;
};

/** i18n keys for the checklist labels, for display. */
export const BRANCH_READINESS_LABEL_KEYS: Record<string, string> = {
  "Applicable Countries": "company:payrolls.applicable_countries",
  "Payroll Policy": "company:payrolls.payroll_policy",
  "Payment Method": "company:payrolls.payment_method",
  WPS: "company:payrolls.wps",
};

/**
 * What the predicate needs: the four booleans, not the config objects.
 *
 * Booleans rather than raw sections so the API-served `readiness` block and the
 * per-branch fallback feed the SAME rules — there is one set of thresholds, and
 * the two data paths cannot drift apart on what "configured" means.
 */
export type BranchReadinessInput = {
  branchId: number;
  /** A country rule is linked AND one of them is flagged default. */
  hasCountryRules: boolean;
  /**
   * Linked rules ignoring the default flag. Only used to pick the message —
   * "none linked" and "linked but none default" are different mistakes.
   */
  countryRuleCount: number;
  hasPayrollPolicy: boolean;
  hasPaymentMethod: boolean;
  hasWps: boolean;
  /**
   * The finer reasons the API serves alongside the booleans (R14/R21). All
   * optional: an older API and the per-branch fallback do not have them, and
   * the generic message is used instead.
   */
  defaultCountryRuleInactive?: boolean;
  payrollPolicyInactive?: boolean;
  wpsIssue?: WpsReadinessIssue | null;
};

const K = (key: string) => `company:payrolls.${key}`;

/** Why WPS is not ready, as the message key the notice shows. */
const WPS_ISSUE_KEYS: Record<WpsReadinessIssue, string> = {
  NOT_CONFIGURED: K("readiness_wps_not_configured"),
  WPS_DISABLED: K("readiness_wps_disabled"),
  ESTABLISHMENT_ID_MISSING: K("readiness_wps_establishment_id_missing"),
  SIF_BANK_MISSING: K("readiness_wps_sif_bank_missing"),
  SIF_BANK_INACTIVE: K("readiness_wps_sif_bank_inactive"),
};

/**
 * Every run-critical requirement, satisfied or not, in branch sub-tab order.
 *
 * Returns the full checklist rather than only the failures so the completion
 * count has a denominator that cannot drift: add a fifth check here and both
 * the gap list and the percentage pick it up. A hardcoded
 * `TOTAL_CHECKS = 4` next to a growing list of `if` statements is exactly the
 * kind of thing that silently starts reporting 100% of the wrong total.
 */
export const computeBranchReadinessChecks = ({
  branchId,
  hasCountryRules,
  countryRuleCount,
  hasPayrollPolicy,
  hasPaymentMethod,
  hasWps,
  defaultCountryRuleInactive,
  payrollPolicyInactive,
  wpsIssue,
}: BranchReadinessInput): BranchReadinessCheck[] => {
  const link = (sTab: BranchSettingsTab) =>
    MS.PAYROLL.BRANCH_TAB(branchId, sTab);

  return [
    {
      label: "Applicable Countries",
      // Distinct failures, one requirement — say which one happened.
      consequence: defaultCountryRuleInactive
        ? K("readiness_country_default_inactive")
        : countryRuleCount > 0
          ? K("readiness_country_no_default")
          : K("readiness_country_none"),
      // Create refuses a branch without an active default (R21).
      blocking: true,
      href: link("country-settings"),
      satisfied: hasCountryRules,
    },
    {
      label: "Payroll Policy",
      consequence: payrollPolicyInactive
        ? K("readiness_policy_inactive")
        : K("readiness_policy_missing"),
      blocking: true,
      href: link("payroll-policy"),
      satisfied: hasPayrollPolicy,
    },
    {
      label: "Payment Method",
      // Not "files cannot be produced" (R21): items fall back to WPS.
      consequence: K("readiness_payment_method_missing"),
      blocking: false,
      href: link("payment-method"),
      satisfied: hasPaymentMethod,
    },
    {
      // The API decides whether WPS is needed at all (`wpsRequired`): off is a
      // complete answer only when nothing on the branch pays by WPS (R14).
      // Not blocking — a run can be created, and approval refuses WPS items.
      label: "WPS",
      consequence: WPS_ISSUE_KEYS[wpsIssue ?? "NOT_CONFIGURED"],
      blocking: false,
      href: link("wps"),
      satisfied: hasWps,
    },
  ];
};

/**
 * The unmet requirements out of a checklist, in the order they appear in it.
 *
 * `satisfied` is dropped rather than carried through: on a gap it is always
 * `false`, and leaving it there invites a caller to branch on it.
 */
const toGaps = (checks: BranchReadinessCheck[]): BranchReadinessGap[] =>
  checks
    .filter((c) => !c.satisfied)
    .map(({ label, consequence, blocking, href }) => ({
      label,
      consequence,
      blocking,
      href,
    }));

/**
 * The unmet requirements, in branch sub-tab order — so a notice listing them
 * reads in the same order as the screens the user is about to visit.
 */
export const computeBranchReadinessGaps = (
  input: BranchReadinessInput,
): BranchReadinessGap[] => toGaps(computeBranchReadinessChecks(input));

export const useBranchPayrollReadiness = (
  branchId: number | null,
): BranchPayrollReadiness => {
  const skip = branchId === null;
  const id = branchId ?? 0;

  const configs = useGetBranchConfigsQuery(undefined, { skip });
  const branch = configs.data?.data?.find((b) => b.id === branchId);
  const served = branch?.readiness;

  /**
   * Only fetched when the list did not carry `readiness`. `configs.isLoading`
   * is part of the skip so the three requests are not fired during the window
   * before the list has answered — otherwise every branch would issue them once
   * on mount and the saving would be lost.
   */
  // `isSuccess`, not merely "not loading": a FAILED list (403) must not fan
  // out into three more requests that fail the same way.
  const needsFallback = !skip && configs.isSuccess && !served;
  const fallbackSkip = !needsFallback;

  const countryRules = useGetBranchCountryRulesQuery(id, {
    skip: fallbackSkip,
  });
  const paymentMethod = useGetBranchPaymentMethodQuery(id, {
    skip: fallbackSkip,
  });
  const wps = useGetBranchWpsQuery(id, { skip: fallbackSkip });

  const isLoading =
    !skip &&
    (configs.isLoading ||
      (needsFallback &&
        (countryRules.isLoading || paymentMethod.isLoading || wps.isLoading)));

  const isUnavailable =
    !skip &&
    !isLoading &&
    (configs.isError ||
      (needsFallback &&
        (countryRules.isError || paymentMethod.isError || wps.isError)));

  const rules = countryRules.data?.data;

  const input = useMemo<BranchReadinessInput | null>(() => {
    if (skip || isLoading || isUnavailable) return null;

    if (served) {
      return {
        branchId: id,
        hasCountryRules: served.hasCountryRules,
        countryRuleCount: served.countryRuleCount,
        hasPayrollPolicy: served.hasPayrollPolicy,
        hasPaymentMethod: served.hasPaymentMethod,
        hasWps: served.hasWps,
        defaultCountryRuleInactive: served.defaultCountryRuleInactive,
        payrollPolicyInactive: served.payrollPolicyInactive,
        wpsIssue: served.wpsIssue,
      };
    }

    return {
      branchId: id,
      hasCountryRules: !!rules?.some((r) => r.isDefault),
      countryRuleCount: rules?.length ?? 0,
      hasPayrollPolicy: !!branch?.payrollPolicy,
      hasPaymentMethod: !!paymentMethod.data?.data,
      hasWps: !!wps.data?.data,
    };
  }, [
    skip,
    isLoading,
    isUnavailable,
    served,
    id,
    rules,
    branch?.payrollPolicy,
    paymentMethod.data,
    wps.data,
  ]);

  return useMemo(() => {
    const checks = input ? computeBranchReadinessChecks(input) : [];
    const gaps = toGaps(checks);
    const totalCount = checks.length;
    const completedCount = totalCount - gaps.length;

    return {
      isLoading,
      gaps,
      blockingGaps: gaps.filter((g) => g.blocking),
      isReady: !skip && !isLoading && checks.length > 0 && gaps.length === 0,
      completedCount,
      totalCount,
      percentComplete: totalCount
        ? Math.round((completedCount / totalCount) * 100)
        : 0,
      settingsHref: MS.PAYROLL.BRANCH_TAB(id),
      isUnavailable,
      payrollPeriodIssue: served?.payrollPeriodIssue ?? null,
    };
  }, [input, isLoading, isUnavailable, skip, id, served]);
};
